import React from 'react';
import * as Yup from 'yup';
import { FormattedMessage } from 'react-intl';
import * as files from './files'
import * as phoneValidator from 'libphonenumber-js';
import moment from 'moment';
import { isValidUuid } from "./uuid";
import { EMAIL_CAMPAIGN_RECIPIENTS_TYPES } from '../config/constants';

const localityNamePattern = /^[A-Za-z0-9\u0080-\u024F](?:[A-Za-z0-9\u0080-\u024F\s.'()-]*[A-Za-z0-9\u0080-\u024F)])?$/;

export const emailRules = Yup.string()
  .matches(
    /^[a-zA-Z0-9._+-]+@([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/,
    {
      message: <FormattedMessage id="validation.email_valid"/>,
    }
  )
  .email( <FormattedMessage id="validation.email_valid"/> )
  .required( <FormattedMessage id="validation.email_required"/> );

export const passwordRules = Yup.string()
  .required( <FormattedMessage id="validation.password_required"/> );

export const oldPasswordRules = Yup.string()
  .required( <FormattedMessage id="validation.old_password_required"/> );

export const passwordRegisterRules = passwordRules
  .matches(
    /^.{8,}$/,
    {
      message: <FormattedMessage id="validation.password_characters"/>,
      excludeEmptyString: true
    }
  )
  .matches(
    /^(?=.*[A-Z]).*$/,
    {
      message: <FormattedMessage id="validation.password_uppercase"/>,
      excludeEmptyString: true
    }
  )
  .matches(
    /^(?=.*[a-z]).*$/,
    {
      message: <FormattedMessage id="validation.password_lowercase"/>,
      excludeEmptyString: true
    }
  )
  .matches(
    /^(?=.*\d).*$/,
    {
      message: <FormattedMessage id="validation.password_number"/>,
      excludeEmptyString: true
    }
  )
  .matches(
    /^(?=.*[@$!%*#?&()[\]{}_\-+=."';:<>]).*$/,
    {
      message: <FormattedMessage id="validation.password_special_char"/>,
      excludeEmptyString: true
    }
  );

export const newPasswordRules = Yup.string()
  .required( <FormattedMessage id="validation.new_password_required"/> )
  .matches(
    /^.{8,}$/,
    {
      message: <FormattedMessage id="validation.new_password_characters"/>,
      excludeEmptyString: true
    }
  )
  .matches(
    /^(?=.*[A-Z]).*$/,
    {
      message: <FormattedMessage id="validation.new_password_uppercase"/>,
      excludeEmptyString: true
    }
  )
  .matches(
    /^(?=.*[a-z]).*$/,
    {
      message: <FormattedMessage id="validation.new_password_lowercase"/>,
      excludeEmptyString: true
    }
  )
  .matches(
    /^(?=.*\d).*$/,
    {
      message: <FormattedMessage id="validation.new_password_number"/>,
      excludeEmptyString: true
    }
  )
  .matches(
    /^(?=.*[@$!%*#?&()[\]{}_\-+=."';:<>]).*$/,
    {
      message: <FormattedMessage id="validation.new_password_special_char"/>,
      excludeEmptyString: true
    }
  );

export const passwordConfirmationRules = Yup.string()
  .oneOf( [ Yup.ref( 'password' ), null ], <FormattedMessage
    id="validation.password_mismatch"/> )
  .required( <FormattedMessage
    id="validation.confirmation_password_required"/> );

export const changePasswordConfirmationRules = Yup.string()
  .oneOf( [ Yup.ref( 'new_password' ), null ], <FormattedMessage
    id="validation.new_password_mismatch"/> )
  .required( <FormattedMessage
    id="validation.confirmation_password_required"/> );

export const firstNameRules = Yup.string()
  .trim()
  .required( <FormattedMessage id="validation.first_name"/> )
  .min( 3, <FormattedMessage id="validation.first_name_min_length"/>)
  .max( 42, <FormattedMessage id="validation.first_name_length"/> )
  .matches(
    /^[a-zA-ZăĂțȚșȘşŞţŢőŐűŰàáâäãåąčćęèéêëėįìíîïłńòóôöõøùúûüųūÿýżźñçčšžÀÁÂÄÃÅĄĆČĖĘÈÉÊËÌÍÎÏĮŁŃÒÓÔÖÕØÙÚÛÜŲŪŸÝŻŹÑßÇŒÆČŠŽ∂ð '-]+$/u,
    {
      message: <FormattedMessage id="validation.first_name.special_characters"/>,
      excludeEmptyString: true
    }
  );

export const lastNameRules = Yup.string()
  .trim()
  .required( <FormattedMessage id="validation.last_name"/> )
  .min( 3, <FormattedMessage id="validation.last_name_min_length"/>)
  .max( 42, <FormattedMessage id="validation.last_name_length"/> )
  .matches(
    /^[a-zA-ZăĂțȚșȘşŞţŢőŐűŰàáâäãåąčćęèéêëėįìíîïłńòóôöõøùúûüųūÿýżźñçčšžÀÁÂÄÃÅĄĆČĖĘÈÉÊËÌÍÎÏĮŁŃÒÓÔÖÕØÙÚÛÜŲŪŸÝŻŹÑßÇŒÆČŠŽ∂ð '-]+$/u,
    {
      message: <FormattedMessage id="validation.last_name.special_characters"/>,
      excludeEmptyString: true
    }
  );

export const fullNameRules = Yup.string()
  .max( 255, <FormattedMessage id="validation.full_name_length"/> )
  .required( <FormattedMessage id="validation.full_name"/> );

export const tokenRules = Yup.string()
  .typeError( <FormattedMessage
    id="validation.verification_code_must_be_string"/> )
  .required( <FormattedMessage id="validation.verification_code_required"/> );

const alphaNum = /^[A-Za-zÀ-ÖØ-öø-ÿĂă0-9_]+( [A-Za-zÀ-ÖØ-öø-ÿĂă0-9_]+)*$/;

export const organizationNameRegex = /^[a-zA-ZăĂțȚșȘşŞţŢőŐűŰàáâäãåąčćęèéêëėįìíîïłńòóôöõøùúûüųūÿýżźñçčšžÀÁÂÄÃÅĄĆČĖĘÈÉÊËÌÍÎÏĮŁŃÒÓÔÖÕØÙÚÛÜŲŪŸÝŻŹÑßÇŒÆČŠŽ∂ð0-9"“”„‟?'’#%()-]([a-zA-ZăĂțȚșȘşŞţŢőŐűŰàáâäãåąčćęèéêëėįìíîïłńòóôöõøùúûüųūÿýżźñçčšžÀÁÂÄÃÅĄĆČĖĘÈÉÊËÌÍÎÏĮŁŃÒÓÔÖÕØÙÚÛÜŲŪŸÝŻŹÑßÇŒÆČŠŽ∂ð0-9]|[- .,&*+"“”„‟?'’#%()])*$/u;

export const organizationNameRules = Yup.string()
  .required(<FormattedMessage id="validation.required_organization_name"/>)
  .matches( organizationNameRegex, {
    message: <FormattedMessage id="validation.valid_organization_name"/>,
    excludeEmptyString: true
  } );

export const termsAndConditionsRules = Yup.boolean()
  .when( 'is_organization', {
      is: false,
      then: Yup.boolean()
        .test( 'terms_and_conditions', <FormattedMessage
          id="validation.terms_and_conditions"/>, (value) => {
          return value;
        } )
    }
  );

export const organizationTermsAndConditionsRules = Yup.boolean()
  .test( 'terms_and_conditions', <FormattedMessage
      id="validation.terms_and_conditions"/>,
    (value) => {
      return !! ( typeof value !== 'undefined' && value );
    }
  );

export const addressRules = Yup.string()
  .min(5, <FormattedMessage id="validation.address_min_length"/>)
  .max(50, <FormattedMessage id="validation.address_max_length"/>)
  .required( <FormattedMessage id="validation.address_required"/> )
  .matches(
    /^[A-Za-zÀ-ÖØ-öø-įĴ-őŔ-žǍ-ǰǴ-ǵǸ-țȞ-ȟȤ-ȳɃɆ-ɏḀ-ẞƀ-ƓƗ-ƚƝ-ơƤ-ƥƫ-ưƲ-ƶẠ-ỿ0-9#./&,-\s()]*$/,
    {
      message: <FormattedMessage id="validation.address_format_alphanumeric"/>,
      excludeEmptyString: true
    }
  )
  .matches(
    /^(?=.*[A-Za-zÀ-ÖØ-öø-įĴ-őŔ-žǍ-ǰǴ-ǵǸ-țȞ-ȟȤ-ȳɃɆ-ɏḀ-ẞƀ-ƓƗ-ƚƝ-ơƤ-ƥƫ-ưƲ-ƶẠ-ỿ])/u,
    {
      message: <FormattedMessage id="validation.address_at_least_one_alpha_char"/>,
      excludeEmptyString: true
    }
  );

export const address2Rules = Yup.string()
  .max(50, <FormattedMessage id="validation.address2_max_length"/>)
  .matches(
    /^[A-Za-zÀ-ÖØ-öø-įĴ-őŔ-žǍ-ǰǴ-ǵǸ-țȞ-ȟȤ-ȳɃɆ-ɏḀ-ẞƀ-ƓƗ-ƚƝ-ơƤ-ƥƫ-ưƲ-ƶẠ-ỿ0-9#./&,-\s()]*$/,
    {
      message: <FormattedMessage id="validation.address2_format_alphanumeric"/>,
      excludeEmptyString: true
    }
  )
  .matches(
    /^(?=.*[A-Za-zÀ-ÖØ-öø-įĴ-őŔ-žǍ-ǰǴ-ǵǸ-țȞ-ȟȤ-ȳɃɆ-ɏḀ-ẞƀ-ƓƗ-ƚƝ-ơƤ-ƥƫ-ưƲ-ƶẠ-ỿ])/u,
    {
      message: <FormattedMessage id="validation.address2_at_least_one_alpha_char"/>,
      excludeEmptyString: true
    }
  );

export const countryRules = Yup.string()
  .typeError( <FormattedMessage id="validation.country_string_validation"/> )
  .matches(
    /^[A-Za-zÀ-ÖØ-öø-ÿ \- ]*$/,
    {
      message: <FormattedMessage id="validation.country_string_validation"/>,
      excludeEmptyString: true
    }
  )
  .required( <FormattedMessage id="validation.country_required"/> );

export const organizationPhoneRules = Yup.string()
  .required( <FormattedMessage id="validation.phone_required"/> )
  .test( 'phone', <FormattedMessage
    id="validation.phone_invalid"/>, (value) => {
    try {
      return ( phoneValidator.isValidNumber( value ) || phoneValidator.isValidNumber( value, 'RO' ) );
    } catch (e) {
      return false;
    }
  } );

export const phoneRules = Yup.string()
  .required( <FormattedMessage id="validation.phone_required"/> )
  .test( 'phone', <FormattedMessage
    id="validation.phone_invalid"/>, (value) => {
    try {
      return value && value.length > 0 ? ( phoneValidator.isValidNumber( value ) || phoneValidator.isValidNumber( value, 'RO' ) ) : true;
    } catch (e) {
      return false;
    }
  } );

export const billingPhoneRules = Yup.string()
  .required( <FormattedMessage id="validation.phone_required"/> )
  .test( 'phone', <FormattedMessage
    id="validation.billing_phone_invalid"/>, (value) => {
      try {
        if (value[0] !== '0' && value[1] !== '0') {
          return false;
        }

        value = '+' + value.substring(2);

        return value && value.length > 0 ? ( phoneValidator.isValidNumber( value ) ) : true;
      } catch (e) {
        return false;
      }
  } );

export const documentGroupRules = Yup.string()
  .test( 'documentGroup', <FormattedMessage
    id="validation.invalid_uuid"/>, isValidUuid )
  .required( <FormattedMessage id="validation.document_group_required"/> )

export const groupNameRules = Yup.string()
  .max( 250, <FormattedMessage id="validation.group_name_length"/> )
  .matches( alphaNum, {
    message: <FormattedMessage id="validation.valid_group_name"/>,
    excludeEmptyString: true
  } )
  .required( <FormattedMessage id="validation.group_name_required"/> );

export const documentGroupNameRules = Yup.string()
  .min( 5, <FormattedMessage id="validation.package_name_min_length"/> )
  .max( 255, <FormattedMessage id="validation.package_name_max_length"/> )
  .required( <FormattedMessage id="validation.document_group_name_required"/> );

export const phoneOptionalRules = Yup.string()
  .test( 'phone', <FormattedMessage
    id="validation.phone_invalid"/>, (value) => {
    try {
      return value && value.length > 0 ? ( phoneValidator.isValidNumber( value ) || phoneValidator.isValidNumber( value, 'RO' ) ) : true;
    } catch (e) {
      return false;
    }
  } );

export const companyCityRules = Yup.string()
  .test( 'companyCity', <FormattedMessage
    id="validation.company_city_required"/>, value => {
    return value && value.trim().length >= 1
  } ).matches(
    localityNamePattern,
    {
      message: <FormattedMessage id="validation.company_city_format"/>,
      excludeEmptyString: true
    }
  )
  .required( <FormattedMessage id="validation.company_city_required"/> );

export const companyCountyRules = Yup.string()
  .test( 'companyCounty', <FormattedMessage
    id="validation.company_county_required"/>, value => {
    return value && value.trim().length >= 1
  } ).matches(
    /^([a-zA-Z\u0080-\u024F\s]+(?:. |-| |'))*[a-zA-Z\u0080-\u024F\s]*$/,
    {
      message: <FormattedMessage id="validation.company_county_format"/>,
      excludeEmptyString: true
    }
  )
  .required( <FormattedMessage id="validation.company_county_required"/> );

let minProducts = 1;

export const noOfProductsRules = Yup.number()
  .when( 'minProducts', {
    is: (value) => {
      minProducts = value;
      return true;
    },
    then: Yup.number().test(
      'noOfProducts', <FormattedMessage
        id="validation.minimum_products_by_product"/>,
      value => {
        return value >= minProducts;
      }
    )
  } )
  .min( 1, <FormattedMessage id="validation.required_num_of_products"/> )
  .max( 2000, <FormattedMessage id="validation.max_num_of_products"/> )
  .required( <FormattedMessage id="validation.required_num_of_products"/> );

// export const noOfProductsRules = Yup.number().min(Yup.ref('noOfProducts'));

export const cuiRules = Yup.mixed()
  .required(<FormattedMessage id="validation.valid_company_cui_required"/>)
  .test('companyCui', <FormattedMessage id="validation.cui_max_length" />, value => {
    return value && value.length <= 50
  })
  .when( 'companyCountry', {
    is: value => {
      if (! value || ( value && value.length === 0 )) {
        return false;
      }

      const acceptedCountries = [ 'ro' ];
      let countryName = value.toLowerCase();

      return acceptedCountries.indexOf( countryName ) !== -1
    },
    then: Yup.string()
      .test( 'companyCui', <FormattedMessage id="validation.valid_company_cui_valid"/>, value => {
        if (! value || ( value && ! value.length )) {
          return false;
        }

        let cif;

        if (! value.isNumber) {
          cif = value.toUpperCase();

          if (cif.indexOf( 'RO' ) === 0) {
            cif = cif.substring( 2, cif.length );
          }

          if (! /^[0-9]+$/.test(cif)) {
            return false;
          }

          cif = parseInt( cif, 10 );
        }

        if (cif.toString().length > 10 || cif.toString().length < 2) {
          return false;
        }

        // control number
        let controlNumber = 753217532;

        // extract control digit
        let controlDigit1 = cif % 10;
        cif = parseInt( ( cif / 10 ), 10 );

        // execute operations on digits
        let t = 0;

        while (cif > 0) {
          t += ( cif % 10 ) * ( controlNumber % 10 );
          cif = parseInt( ( cif / 10 ), 10 );
          controlNumber = parseInt( ( controlNumber / 10 ), 10 );
        }

        // multiply with 10 and find modulo 11
        let controlDigit2 = t * 10 % 11;

        // if modulo 11 is 10, then control digit is 0
        if (controlDigit2 === 10) {
          controlDigit2 = 0;
        }

        return controlDigit1 === controlDigit2;
      } )
  } );

export const registryNoRules = Yup.mixed()
  .test('companyRegistryNo', <FormattedMessage id="validation.register_no_max_length" />, value => {
    if (! value) {
      return true;
    }

    return value.length <= 50;
  })
  .when( 'companyCountry', {
    is: value => {
      if (! value || ( value && value.length === 0 )) {
        return false;
      }

      const acceptedCountries = [ 'ro' ];
      let countryName = value.toLowerCase();

      return acceptedCountries.indexOf( countryName ) !== -1
    },
    then: Yup.string()
      .test( 'companyRegistryNo', <FormattedMessage
        id="validation.valid_company_registry_no_valid"/>, value => {
        if (! value || ( value && ! value.length )) {
          return true;
        }

        // accepted indicatives for Romania
        const indicatives = [ 'J', 'F', 'C' ];
        let registryNo = value.toUpperCase();
        const inputIndicative = registryNo.charAt( 0 );

        // Check if indicative is valid
        if (indicatives.indexOf( inputIndicative ) === -1) {
          return false;
        }

        const registryNoWithoutIndicative = registryNo.substring( 1 );

        // Check for new format: continuous number (e.g., J2015011113403)
        if (/^\d+$/.test(registryNoWithoutIndicative)) {
          // Must be at least 6 digits and max 20 digits for continuous format
          return registryNoWithoutIndicative.length >= 6 && registryNoWithoutIndicative.length <= 20;
        }

        // Check for traditional format with slashes
        const registryNoParts = registryNoWithoutIndicative.split( "/" );

        // registry no should be composed by 3 elements separated by / char
        let validation = registryNoParts.length !== 3;
        // first segment number should be between 0 and 52
        validation = validation || ( registryNoParts[0] < 1 || registryNoParts[0] > 52 );
        // if first segment number is between 1 and 9, it should be 0 lead number
        validation = validation || ( registryNoParts[0] < 10 && parseInt( registryNoParts[0].charAt( 0 ), 10 ) !== 0 );
        // second registry no segment should be number greater than 0
        validation = validation || ( isNaN( Number( registryNoParts[1] ) ) || Number( registryNoParts[1] ) < 0 );
        // the last registry no should be an year in the past of current year
        // or full date with dot separator
        const registryNoPartsYear = moment(registryNoParts[2], "DD.MM.YYYY", true)

        if (registryNoParts[2]) {
          if (registryNoParts[2].toString().length === 4) {
            validation = validation || (isNaN(Number(registryNoParts[2])) || Number(registryNoParts[2]) < 1900 || Number(registryNoParts[2]) > new Date().getFullYear());
          } else if (registryNoParts[2].toString().length === 10) {
            validation = validation || !(registryNoPartsYear.isValid() && registryNoPartsYear.isSameOrBefore(moment()))
          } else {
            validation = true;
          }
        } else {
          validation = true;
        }

        return !validation;
      } )
  } );

export const companyRules = Yup.string()
  .required( <FormattedMessage id="validation.company_name_required"/> );

export const reasonRules = Yup.string()
  .required( <FormattedMessage id="validation.reason"/> );

export const messageRules = Yup.string()
  .min( 5, <FormattedMessage id="validation.message_min_length"/> )
  .max( 1000, <FormattedMessage id="validation.message_max_length"/> )
  .required( <FormattedMessage id="validation.message"/> );

export const expireHourFinishSDSRules = Yup.mixed()
  .when( 'hasExpire', {
    is: value => value,
    then: Yup.mixed()
      .test( 'expiresAtServer', <FormattedMessage
        id="validation.expiry_date_in_past"/>, value => {
        if (Array.isArray( value ) && value.length === 3) {
          try {
            return moment( value[0] ).hours( value[1] ).minutes( value[2] ).isAfter( moment() );
          } catch (e) {
            return false;
          }
        }

        return false;
      } )
  } );

export const expireHourRules = Yup.mixed()
  .test( 'expiresAtServer', <FormattedMessage
    id="validation.expiry_date_in_past"/>, value => {
    if (Array.isArray( value ) && value.length === 3) {
      try {
        return moment( value[0] ).hours( value[1] ).minutes( value[2] ).isAfter( moment() );
      } catch (e) {
        return false;
      }
    }

    return false;
  } );

export const packageNameRules = Yup.string()
  .min( 1, <FormattedMessage id="validation.package_name_min_length"/> )
  .max( 255, <FormattedMessage id="validation.package_name_max_length"/> )
  .required( <FormattedMessage id="validation.package_name_required"/> )
  .test( 'packageName', <FormattedMessage
    id="validation.packageName_filename_validation"/>, files.fileNameValidation );

export const documentNameRules = Yup.string()
  .min( 1, <FormattedMessage id="validation.document_name_min_length"/> )
  .max( 255, <FormattedMessage id="validation.document_name_max_length"/> )
  .required( <FormattedMessage id="validation.document_name_required"/> )
  .test( 'packageName', <FormattedMessage
    id="validation.documentName_filename_validation"/>, files.fileNameValidation );

export const documentLanguageRules = Yup.string()
  .test( 'documentGroup', <FormattedMessage
    id="validation.invalid_uuid"/>, isValidUuid )
  .required( <FormattedMessage id="validation.document_language_required"/> )

export const termsRules = Yup.boolean()
  .required( <FormattedMessage id="validation.terms"/> )
  .test( 'terms', <FormattedMessage id="validation.terms"/>, value => value );

export const gdprRules = Yup.boolean()
  .required( <FormattedMessage id="validation.gdpr"/> )
  .test( 'gdpr', <FormattedMessage id="validation.gdpr"/>, value => value );

export const captchaRules = Yup.boolean()
  .required( <FormattedMessage id="validation.captcha"/> )
  .test( 'captcha', <FormattedMessage
    id="validation.captcha"/>, value => value );

export const organizationSelectRules = Yup.string()
  .required( <FormattedMessage id="validation.select_organization"/> );

export const userForPaymentSelectRules = Yup.string()
  .required( <FormattedMessage id="validation.select_user"/> );

export const productGroupSelectRules = Yup.array()
  .min( 1, <FormattedMessage id="validation.select_product_group"/> );

export const simpleSignaturesNumber = Yup.string()
  .when( 'isSimpleSignature', {
    is: (value) => {
      return value;
    },
    then: Yup.string()
      .test(
        'simpleSignaturesNumber', <FormattedMessage
          id="validation.simple_signatures.number.required"/>,
        value => {
          if (value <= 0 || ! value) {
            return false;
          }
          return true;
        } )
      .matches(
        /^[1-9]\d*$/,
        {
          message: <FormattedMessage
            id="validation.simple_signatures.number_format"/>,
          excludeEmptyString: true
        }
      )
  } );

export const simpleSignaturesPriceValue = Yup.string()
  .when( 'isSimpleSignature', {
    is: (value) => {
      return value;
    },
    then: Yup.string()
      .test(
        'simpleSignaturesValue', <FormattedMessage
          id="validation.simple_signatures.price.value.required"/>,
        value => {

          if (value <= 0 || ! value) {
            return false;
          }
          return true;
        } )
      .matches(
        /^-?\d*(\.\d+)?$/,
        {
          message: <FormattedMessage
            id="validation.simple_signatures.price.number_format"/>,
          excludeEmptyString: true
        }
      )
  } );

export const qualifiedSignaturesNumber = Yup.string()
  .when( 'isQualifiedSignature', {
    is: (value) => {
      return value;
    },
    then: Yup.string()
      .test(
        'simpleSignaturesNumber', <FormattedMessage
          id="validation.qualified_signatures.number.required"/>,
        value => {
          if (value <= 0 || ! value) {
            return false;
          }
          return true;
        } )
      .matches(
        /^[1-9]\d*$/,
        {
          message: <FormattedMessage
            id="validation.qualified_signatures.number_format"/>,
          excludeEmptyString: true
        }
      )
  } );

export const qualifiedSignaturesPriceValue = Yup.string()
  .when( 'isQualifiedSignature', {
    is: (value) => {
      return value;
    },
    then: Yup.string()
      .test(
        'simpleSignaturesValue', <FormattedMessage
          id="validation.qualified_signatures.price.value.required"/>,
        value => {

          if (value <= 0 || ! value) {
            return false;
          }
          return true;
        } )
      .matches(
        /^-?\d*(\.\d+)?$/,
        {
          message: <FormattedMessage
            id="validation.qualified_signatures.price.number_format"/>,
          excludeEmptyString: true
        }
      )
  } );

let maxCertificateNumber;
export const certificatesNumber = Yup.string()
  .when( 'isCertificate', {
    is: (value) => {
      return value;
    },
    then: Yup.string()
      .test(
        'simpleSignaturesNumber', <FormattedMessage
          id="validation.certificates.number.required"/>,
        value => {
          if (value <= 0 || ! value) {
            return false;
          }
          return true;
        } )
      .matches(
        /^[1-9]\d*$/,
        {
          message: <FormattedMessage
            id="validation.certificates.number_format"/>,
          excludeEmptyString: true
        }
      )
      .when( 'maxCertificateNumber', {
        is: (value) => {
          maxCertificateNumber = value;
          return true;
        },
        then: Yup.string()
          .test(
            'simpleSignaturesNumber', <FormattedMessage
              id="validation.certificates.bigger"/>,
            value => {
              if (value > maxCertificateNumber) {
                return false;
              }

              return true;
            }
          )
      } )
  } );

export const certificatesPriceValue = Yup.string()
  .when( 'isCertificate', {
    is: (value) => {
      return value;
    },
    then: Yup.string()
      .test(
        'simpleSignaturesValue', <FormattedMessage
          id="validation.certificates.price.value.required"/>,
        value => {

          if (value <= 0 || ! value) {
            return false;
          }
          return true;
        } )
      .matches(
        /^-?\d*(\.\d+)?$/,
        {
          message: <FormattedMessage
            id="validation.certificates.price.number_format"/>,
          excludeEmptyString: true
        }
      )
  } );

export const userWithCertificatesRules = Yup.array()
  .when( 'isCertificate', {
    is: (value) => {
      if (value) {
        return true;
      } else {
        return false;
      }
    },
    then: Yup.array()
      .min( 1, <FormattedMessage id="validation.certificates.no_user"/> )
      .test(
        'usersWithCertificate', <FormattedMessage
          id="validation.certificates.nr_equal_user"/>,
        function (value) {
          const certificatesNumber = this.parent.certificatesNumber;
          if (parseInt(value.length) !== parseInt(certificatesNumber)) {
            return false;
          }

          return true;
        }
      )
  } );

// Validation rules for certificates with authentication
let maxCertificateWithAuthNumber;
export const certificatesWithAuthNumber = Yup.string()
  .when( 'isCertificateWithAuth', {
    is: (value) => {
      return value;
    },
    then: Yup.string()
      .test(
        'certificatesWithAuthNumber', <FormattedMessage
          id="validation.certificates.number.required"/>,
        value => {
          if (value <= 0 || ! value) {
            return false;
          }
          return true;
        } )
      .matches(
        /^[1-9]\d*$/,
        {
          message: <FormattedMessage
            id="validation.certificates.number_format"/>,
          excludeEmptyString: true
        }
      )
      .when( 'maxCertificateNumber', {
        is: (value) => {
          maxCertificateWithAuthNumber = value;
          return true;
        },
        then: Yup.string()
          .test(
            'certificatesWithAuthNumber', <FormattedMessage
              id="validation.certificates.bigger"/>,
            value => {
              if (value > maxCertificateWithAuthNumber) {
                return false;
              }

              return true;
            }
          )
      } )
  } );

export const certificatesWithAuthValue = Yup.string()
  .when( 'isCertificateWithAuth', {
    is: (value) => {
      return value;
    },
    then: Yup.string()
      .test(
        'certificatesWithAuthValue', <FormattedMessage
          id="validation.certificates.price.value.required"/>,
        value => {

          if (value <= 0 || ! value) {
            return false;
          }
          return true;
        } )
      .matches(
        /^-?\d*(\.\d+)?$/,
        {
          message: <FormattedMessage
            id="validation.certificates.price.number_format"/>,
          excludeEmptyString: true
        }
      )
  } );

export const userWithCertificatesWithAuthRules = Yup.array()
  .when( 'isCertificateWithAuth', {
    is: (value) => {
      if (value) {
        return true;
      } else {
        return false;
      }
    },
    then: Yup.array()
      .min( 1, <FormattedMessage id="validation.certificates.no_user"/> )
      .test(
        'usersWithCertificateWithAuth', <FormattedMessage
          id="validation.certificates.nr_equal_user"/>,
        function (value) {
          const certificatesWithAuthNumber = this.parent.certificatesWithAuthNumber;
          if (parseInt(value.length) !== parseInt(certificatesWithAuthNumber)) {
            return false;
          }

          return true;
        }
      )
  } );

export const emailTemplateSelectRules = Yup.string()
  .required( <FormattedMessage id="validation.select_email_template"/> );

export const testRules = Yup.boolean()
  .required( <FormattedMessage id="validation.test"/> );

export const campaignName = Yup.string()
  .trim()
  .required(<FormattedMessage id="validation.required" values={
    {
      fieldName: <FormattedMessage id="fields.campaignManagement.name">{ texts => {
        texts.forEach((text, index) => texts[index] = text.toLowerCase());
        return texts;
      } }</FormattedMessage>
    }
  } />)
  .min(3, <FormattedMessage id="validation.min" values={
    {
      fieldName: <FormattedMessage id="fields.campaignManagement.name">{ texts => {
        texts.forEach((text, index) => texts[index] = text.toLowerCase());
        return texts;
      } }</FormattedMessage>,
      len: 3,
    }
  } />)
  .max(255, <FormattedMessage id="validation.max" values={
    {
      fieldName: <FormattedMessage id="fields.campaignManagement.name">{ texts => {
        texts.forEach((text, index) => texts[index] = text.toLowerCase());
        return texts;
      } }</FormattedMessage>,
      len: 255,
    }
  } />)
  .matches(/^[0-9a-zA-ZăĂțȚșȘàáâäãåąčćęèéêëėįìíîïłńòóôöõøùúûüųūÿýżźñçčšžÀÁÂÄÃÅĄĆČĖĘÈÉÊËÌÍÎÏĮŁŃÒÓÔÖÕØÙÚÛÜŲŪŸÝŻŹÑßÇŒÆČŠŽ∂ð\s-]+$/u, {
    message: <FormattedMessage id="validation.alphaNum" values={
      {
        fieldName: <FormattedMessage id="fields.campaignManagement.name">{ texts => {
          texts.forEach((text, index) => texts[index] = text.toLowerCase());
          return texts;
        } }</FormattedMessage>,
      }
    } />,
    excludeEmptyString: true
  } )
;

export const campaignNotes = Yup.string()
  .trim()
  .required(<FormattedMessage id="validation.required" values={
    {
      fieldName: <FormattedMessage id="fields.campaignManagement.notes">{ texts => {
        texts.forEach((text, index) => texts[index] = text.toLowerCase());
        return texts;
      } }</FormattedMessage>
    }
  } />)
  .min(3, <FormattedMessage id="validation.min" values={
    {
      fieldName: <FormattedMessage id="fields.campaignManagement.notes">{ texts => {
        texts.forEach((text, index) => texts[index] = text.toLowerCase());
        return texts;
      } }</FormattedMessage>,
      len: 3,
    }
  } />)
  .max(65535, <FormattedMessage id="validation.max" values={
    {
      fieldName: <FormattedMessage id="fields.campaignManagement.notes">{ texts => {
        texts.forEach((text, index) => texts[index] = text.toLowerCase());
        return texts;
      } }</FormattedMessage>,
      len: 65535,
    }
  } />)
  .matches(/^[0-9a-zA-ZăĂțȚșȘàáâäãåąčćęèéêëėįìíîïłńòóôöõøùúûüųūÿýżźñçčšžÀÁÂÄÃÅĄĆČĖĘÈÉÊËÌÍÎÏĮŁŃÒÓÔÖÕØÙÚÛÜŲŪŸÝŻŹÑßÇŒÆČŠŽ∂ð\s-]+$/u, {
    message: <FormattedMessage id="validation.alphaNum" values={
      {
        fieldName: <FormattedMessage id="fields.campaignManagement.notes">{ texts => {
          texts.forEach((text, index) => texts[index] = text.toLowerCase())
          return texts;
        } }</FormattedMessage>,
      }
    } />,
    excludeEmptyString: true
  })
;

export const campaignStartDate = Yup.string()
  .nullable()
  .required(<FormattedMessage id="validation.required" values={
    {
      fieldName: <FormattedMessage id="fields.campaignManagement.start_date">{ texts => {
        texts.forEach((text, index) => texts[index] = text.toLowerCase());
        return texts;
      } }</FormattedMessage>
    }
  } />)
  .test("start_date", <FormattedMessage id="validation.date_in_past" values={
      {
        fieldName: <FormattedMessage id="fields.campaignManagement.start_date">{ texts => {
          texts.forEach((text, index) => texts[index] = text.toLowerCase())
          return texts;
        } }</FormattedMessage>
      }
    } />, start_date => {
      return moment(start_date).isSameOrAfter(moment(), "day");
  })
;

export const campaignEndDate = Yup.string()
  .nullable()
  .required(<FormattedMessage id="validation.required" values={
    {
      fieldName: <FormattedMessage id="fields.campaignManagement.end_date">{ texts => {
        texts.forEach((text, index) => texts[index] = text.toLowerCase());
        return texts;
      } }</FormattedMessage>
    }
  } />)
  .when("start_date", (start_date, schema) => {
    return schema.test('end_date',
      <FormattedMessage id="validation.date_must_be_after" values={
        {
          fieldName: <FormattedMessage id="fields.campaignManagement.end_date">{ texts => {
            texts.forEach((text, index) => texts[index] = text.toLowerCase());
            return texts;
          } }</FormattedMessage>,
          fieldName2: <FormattedMessage id="fields.campaignManagement.start_date">{ texts => {
            texts.forEach((text, index) => texts[index] = text.toLowerCase());
            return texts;
          } }</FormattedMessage>
        }
      } />, end_date => {
      return moment(end_date).isSameOrAfter(moment(start_date), "day");
    });
  })
;

export const campaignTrackingLabel = Yup.string()
  .required(<FormattedMessage id="validation.required" values={
    {
      fieldName: <FormattedMessage id="fields.campaignManagement.tracking_name">{ texts => {
        texts.forEach((text, index) => texts[index] = text.toLowerCase());
        return texts;
      } }</FormattedMessage>
    }
  } />
);

export const campaignTrackingId = Yup.string()
  .trim()
  .required(<FormattedMessage id="validation.required" values={
    {
      fieldName: <FormattedMessage id="fields.campaignManagement.tracking_id">{ texts => {
        texts.forEach((text, index) => texts[index] = text.toLowerCase());
        return texts;
      } }</FormattedMessage>
    }
  } />)
  .min(3, <FormattedMessage id="validation.min" values={
    {
      fieldName: <FormattedMessage id="fields.campaignManagement.tracking_id">{ texts => {
        texts.forEach((text, index) => texts[index] = text.toLowerCase());
        return texts;
      } }</FormattedMessage>,
      len: 3
    }
  } />)
  .max(255, <FormattedMessage id="validation.max" values={
    {
      fieldName: <FormattedMessage id="fields.campaignManagement.tracking_id">{ texts => {
        texts.forEach((text, index) => texts[index] = text.toLowerCase());
        return texts;
      } }</FormattedMessage>,
      len: 255
    }
  } />)
;

export const noOfCoupons = Yup.number()
  .typeError(<FormattedMessage id="validation.num" values={
    {
      fieldName: <FormattedMessage id="fields.campaignManagement.no_of_coupons" >{ texts => {
        texts.forEach((text, index) => texts[index] = text.toLowerCase());
        return texts;
      } }</FormattedMessage>,
    }
  } />)
  .required(<FormattedMessage id="validation.required" values={
    {
      fieldName: <FormattedMessage id="fields.campaignManagement.no_of_coupons" >{ texts => {
        texts.forEach((text, index) => texts[index] = text.toLowerCase());
        return texts;
      } }</FormattedMessage>,
    }
  } />)
  .min(1, <FormattedMessage id="validation.int.min" values={
    {
      fieldName: <FormattedMessage id="fields.campaignManagement.no_of_coupons" >{ texts => {
        texts.forEach((text, index) => texts[index] = text.toLowerCase());
        return texts;
      } }</FormattedMessage>,
      value: 1,
    }
  } />)
  .max(100, <FormattedMessage id="validation.int.max" values={
    {
      fieldName: <FormattedMessage id="fields.campaignManagement.no_of_coupons">{ texts => {
        texts.forEach((text, index) => texts[index] = text.toLowerCase());
        return texts;
      } }</FormattedMessage>,
      value: 100,
    }
  } />)
;

export const couponPrefix = Yup.string()
  .nullable()
  .matches(/^[a-zA-Z0-9]+$/u, {
    message: <FormattedMessage id="validation.alpha" values={
      {
        fieldName: <FormattedMessage id="fields.campaignManagement.prefix">{ texts => {
          texts.forEach((text, index) => texts[index] = text.toLowerCase())
          return texts;
        } }</FormattedMessage>,
      }
    } />,
    excludeEmptyString: true
  })
  .min(3, <FormattedMessage id="validation.exactly" values={
    {
      fieldName: <FormattedMessage id="fields.campaignManagement.prefix" >{ texts => {
        texts.forEach((text, index) => texts[index] = text.toLowerCase());
        return texts;
      } }</FormattedMessage>,
      len: 3,
    }
  } />)
  .max(3, <FormattedMessage id="validation.exactly" values={
    {
      fieldName: <FormattedMessage id="fields.campaignManagement.prefix">{ texts => {
        texts.forEach((text, index) => texts[index] = text.toLowerCase());
        return texts;
      } }</FormattedMessage>,
      len: 3,
    }
  } />)
;

export const couponType = Yup.number()
  .required(<FormattedMessage id="validation.required" values={
    {
      fieldName: <FormattedMessage id="fields.campaignManagement.type" >{ texts => {
        texts.forEach((text, index) => texts[index] = text.toLowerCase());
        return texts;
      } }</FormattedMessage>,
    }
  } />)
;

export const couponProductId = Yup.string()
  .required(<FormattedMessage id="validation.required" values={
    {
      fieldName: <FormattedMessage id="fields.campaignManagement.productList.package_name" >{ texts => {
        texts.forEach((text, index) => texts[index] = text.toLowerCase());
        return texts;
      } }</FormattedMessage>,
    }
  } />)
;

export const couponProductGroupId = Yup.string()
  .required(<FormattedMessage id="validation.required" values={
    {
      fieldName: <FormattedMessage id="fields.campaignManagement.productList.product" >{ texts => {
        texts.forEach((text, index) => texts[index] = text.toLowerCase());
        return texts;
      } }</FormattedMessage>,
    }
  } />)
;

export const couponDiscountType = Yup.string()
  .required(<FormattedMessage id="validation.required" values={
    {
      fieldName: <FormattedMessage id="fields.campaignManagement.productList.discount_type" >{ texts => {
        texts.forEach((text, index) => texts[index] = text.toLowerCase());
        return texts;
      } }</FormattedMessage>,
    }
  } />)
;

export const couponValue = Yup.number()
  .typeError(<FormattedMessage id="validation.num" values={
    {
      fieldName: <FormattedMessage id="fields.campaignManagement.productList.discount_value" >{ texts => {
        texts.forEach((text, index) => texts[index] = text.toLowerCase());
        return texts;
      } }</FormattedMessage>,
    }
  } />)
  .required(<FormattedMessage id="validation.required" values={
    {
      fieldName: <FormattedMessage id="fields.campaignManagement.productList.discount_value" >{ texts => {
        texts.forEach((text, index) => texts[index] = text.toLowerCase());
        return texts;
      } }</FormattedMessage>,
    }
  } />)
  .min(0, <FormattedMessage id="validation.int.min" values={
    {
      fieldName: <FormattedMessage id="fields.campaignManagement.productList.discount_value" >{ texts => {
        texts.forEach((text, index) => texts[index] = text.toLowerCase());
        return texts;
      } }</FormattedMessage>,
      value: 1,
    }
  } />)
  .max(99, <FormattedMessage id="validation.int.max" values={
    {
      fieldName: <FormattedMessage id="fields.campaignManagement.productList.discount_value">{ texts => {
        texts.forEach((text, index) => texts[index] = text.toLowerCase());
        return texts;
      } }</FormattedMessage>,
      value: 99,
    }
  } />)
;

export const couponProducts = Yup.array()
  .test('coupon_products',
    <FormattedMessage id="fields.campaignManagement.productList.coupon_products.at_least_one_value_positive" />,
    (values) => {
      let atLeastOneGreaterThanZero = false;

      values.forEach((item) => {
        if (parseInt(item.value) > 0) {
          atLeastOneGreaterThanZero = true;
        }
      });

        return atLeastOneGreaterThanZero;
    }
  )
  .of(Yup.object().shape({
    product_group_id: couponProductGroupId,
    product_id: couponProductId,
    discount_type: couponDiscountType,
    value: couponValue,
  })
)
;

export const dayAsTimestamp = Yup.date()
  .nullable()
  .transform( (curr, orig) => orig === '' ? null : curr )
  .min( moment().startOf('day'), <FormattedMessage
    id="validation.day_as_timestamp_min"/> )
  .required( <FormattedMessage id="validation.day_as_timestamp_required"/> )
;

export const description = Yup.string()
  .trim()
  .nullable()
  .max(255, <FormattedMessage id="validation.max" values={
    {
      fieldName: <FormattedMessage id="fields.appointments.description">{ texts => {
        texts.forEach((text, index) => texts[index] = text.toLowerCase());
        return texts;
      } }</FormattedMessage>,
      len: 255,
    }
  } />)
  .matches(/^[0-9a-zA-ZăĂțȚșȘàáâäãåąčćęèéêëėįìíîïłńòóôöõøùúûüųūÿýżźñçčšžÀÁÂÄÃÅĄĆČĖĘÈÉÊËÌÍÎÏĮŁŃÒÓÔÖÕØÙÚÛÜŲŪŸÝŻŹÑßÇŒÆČŠŽ∂ð\s-]+$/u, {
    message: <FormattedMessage id="validation.alphaNum" values={
      {
        fieldName: <FormattedMessage id="fields.appointments.description">{ texts => {
          texts.forEach((text, index) => texts[index] = text.toLowerCase());
          return texts;
        } }</FormattedMessage>,
      }
    } />,
    excludeEmptyString: true
  } )
;

export const passkeyAuth = Yup.string()
  .required( <FormattedMessage id="validation.passkey_auth_required"/> );

export const emailCampaignTemplateSelectRules = Yup.string()
  .required( <FormattedMessage id="validation.select_email_campaign_template"/> );

export const emailCampaignRecipientsSelectRules = Yup.string()
  .required( <FormattedMessage id="validation.select_email_campaign_recipients"/> );

export const emailTestRules = Yup.string()
  .when('recipients', {
    is: EMAIL_CAMPAIGN_RECIPIENTS_TYPES.TEST_USERS,
    then : Yup.string().required( <FormattedMessage id="validation.email_required"/> )
      .matches(
        /^[a-zA-Z0-9._+-]+@([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/,
        {
          message: <FormattedMessage id="validation.email_valid"/>,
        }
      )
      .email( <FormattedMessage id="validation.email_valid"/> )
});

export const selectedUserEmailsRules = Yup.string()
  .when('recipients', {
    is: EMAIL_CAMPAIGN_RECIPIENTS_TYPES.SELECTED_USERS,
    then : Yup.string().required( <FormattedMessage id="validation.selected_user_emails_required"/> )
});
